import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { apiClient } from '../services/apiClient';

type HistoryEntry = {
  sessionId: string;
  subject: string;
  particleCount: number;
  completedAt: string;
};

const SUBJECT_LABEL: Record<string, string> = {
  math: '数学',
  yuwen: '语文',
  english: '英语',
  physics: '物理',
  chemistry: '化学',
  biology: '生物',
  history: '历史',
  geography: '地理',
  politics: '政治',
  chinese: '语文',
};

function subjectLabel(subject: string) {
  return SUBJECT_LABEL[subject] ?? subject;
}

export function LearningGoalsPage() {
  const { user } = useAuth();
  const userId = user?.id || '';

  const [history, setHistory] = useState<HistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ userId, limit: '200' });
      const res = await apiClient.requestJson<{ success: boolean; data?: HistoryEntry[]; error?: string }>(
        `/api/review/learning-episodes/sessions/history?${params.toString()}`,
        { auth: true },
      );
      if (res.success && Array.isArray(res.data)) {
        setHistory(res.data);
      } else {
        setError(res.error ?? '加载历史失败');
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载历史失败');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const subjectStats = useMemo(() => {
    if (!history) return [];
    const map = new Map<string, { sessionCount: number; totalParticles: number }>();
    for (const entry of history) {
      const existing = map.get(entry.subject) ?? { sessionCount: 0, totalParticles: 0 };
      existing.sessionCount += 1;
      existing.totalParticles += entry.particleCount;
      map.set(entry.subject, existing);
    }
    return Array.from(map.entries()).map(([subject, stats]) => ({ subject, ...stats }));
  }, [history]);

  if (!userId) return null;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-ink">学习目标</h1>
        <p className="mt-1 text-sm text-muted">跟踪各学科主干覆盖进展。</p>
      </div>

      <section className="rounded-2xl border border-border bg-surface p-5 shadow-card">
        <h2 className="text-sm font-semibold text-muted">默认学习目标</h2>
        <div className="mt-4 flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-2xl">
            🎯
          </div>
          <div>
            <div className="font-semibold text-ink">覆盖整体学科主干</div>
            <p className="mt-0.5 text-sm text-muted">刷完所有学科主干卡族，建立完整知识体系。</p>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-border bg-surface p-5 text-center text-danger">{error}</div>
      ) : subjectStats.length === 0 ? (
        <div className="rounded-xl border border-border bg-surface p-5 text-center text-sm text-muted">
          完成第一轮学习后，学科进展将显示在这里。
        </div>
      ) : (
        <section className="rounded-2xl border border-border bg-surface p-5 shadow-card">
          <h2 className="mb-3 text-sm font-semibold text-muted">学科进展</h2>
          <ul className="divide-y divide-border">
            {subjectStats.map(({ subject, sessionCount, totalParticles }) => (
              <li key={subject} className="flex items-center justify-between gap-3 py-3">
                <div>
                  <div className="font-semibold text-ink">{subjectLabel(subject)}</div>
                  <div className="text-xs text-muted">{sessionCount} 轮 · {totalParticles} 累计卡族</div>
                </div>
                <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                  全主干覆盖
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
